import {
  collection, doc, getCountFromServer, getDoc, getDocs, orderBy, query, setDoc, Timestamp, where,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import UserService from '@/service/user.service';
import ServiceService from '@/service/service.service';
import { RequestStatus } from '@/types/order';
import type {
  ActivationMetrics, GoalConfig, MonetizationBreakdown, RetentionMetrics, WeeklyAcquisitionMetrics, WeeklyRequestsMetrics, TopServiceWeeklyStat, PlatformBreakdown, AcquisitionChannelBreakdown,
} from '@/types/kpi';

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfWeek(date: Date): Date {
  // Weeks start on Monday
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // convert Sun=0 to Sun=6, Mon=0
  d.setDate(d.getDate() - day);
  return startOfDay(d);
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function labelForWeek(weekStart: Date): string {
  const y = weekStart.getFullYear();
  const m = String(weekStart.getMonth() + 1).padStart(2, '0');
  const d = String(weekStart.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function toTimestamp(d: Date): Timestamp {
  return Timestamp.fromDate(d);
}

function isRequestPaid(status: unknown): boolean {
  const n = typeof status === 'number' ? status : Number(status);
  return Number.isFinite(n) && n >= RequestStatus['In Progress'];
}

const GOALS_COLLECTION = 'kpi-goals';
const GOALS_DOC_ID = 'global';

class KPIService {
  // In-memory caches per-session
  private static weeklyInstallsCache = new Map<number, WeeklyAcquisitionMetrics[]>();

  private static activationRatesCache = new Map<string, ActivationMetrics[]>();

  private static retentionCache = new Map<number, RetentionMetrics[]>();

  private static monetizationCache = new Map<string, MonetizationBreakdown>();

  private static toplineCache: { data: { totalUsers: number; totalRequests: number; paidRequests: number; conversionPaidPct: number } | null } = { data: null };

  private static weeklyRequestsCache = new Map<number, WeeklyRequestsMetrics[]>();

  private static topServicesCache = new Map<number, TopServiceWeeklyStat[]>();

  private static platformCache: PlatformBreakdown | null = null;

  private static paidServicesThisWeekCache: { distinctCount: number; services: Array<{ serviceId: string; serviceTitle?: string; paid: number }> } | null = null;

  private static acquisitionChannelCache: AcquisitionChannelBreakdown | null = null;

  static async getWeeklyInstalls(weeks = 8, useCache = true): Promise<WeeklyAcquisitionMetrics[]> {
    if (useCache && KPIService.weeklyInstallsCache.has(weeks)) {
      const cached = KPIService.weeklyInstallsCache.get(weeks);
      if (cached) return cached;
    }
    const end = endOfDay(new Date());
    const startBoundary = addDays(startOfWeek(new Date()), -7 * (weeks - 1));

    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('registrationDate', '>=', toTimestamp(startBoundary)), orderBy('registrationDate', 'asc'));
    const snap = await getDocs(q);
    const buckets: Record<string, number> = {};

    for (let i = 0; i < weeks; i += 1) {
      const weekStart = addDays(startOfWeek(new Date()), -7 * i);
      buckets[labelForWeek(weekStart)] = 0;
    }

    snap.forEach((docSnap) => {
      const data = docSnap.data() as { registrationDate?: { toDate?: () => Date } };
      if (!data.registrationDate?.toDate) return;
      const reg = data.registrationDate.toDate();
      if (reg > end) return;
      const bucketStart = startOfWeek(reg);
      const label = labelForWeek(bucketStart);
      if (buckets[label] !== undefined) buckets[label] += 1;
    });

    const labels = Object.keys(buckets)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const result = labels.map((l) => ({ weekLabel: l, installs: buckets[l] }));
    KPIService.weeklyInstallsCache.set(weeks, result);
    return result;
  }

  static async getActivationRates(weeks = 8, windowDays = 7, useCache = true): Promise<ActivationMetrics[]> {
    const key = `${weeks}:${windowDays}`;
    if (useCache && KPIService.activationRatesCache.has(key)) {
      const cached = KPIService.activationRatesCache.get(key);
      if (cached) return cached;
    }
    const end = endOfDay(new Date());
    const startBoundary = addDays(startOfWeek(new Date()), -7 * (weeks - 1));
    const usersRef = collection(db, 'users');
    const uq = query(usersRef, where('registrationDate', '>=', toTimestamp(startBoundary)), orderBy('registrationDate', 'asc'));
    const userSnap = await getDocs(uq);

    const usersByWeek: Record<string, Array<{ id: string; regAt: Date }>> = {};
    userSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const regAt = data.registrationDate?.toDate?.();
      if (!regAt || regAt > end) return;
      const label = labelForWeek(startOfWeek(regAt));
      if (!usersByWeek[label]) usersByWeek[label] = [];
      usersByWeek[label].push({ id: docSnap.id, regAt });
    });

    const labels = Object.keys(usersByWeek).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const requestsRef = collection(db, 'requests');
    const results = await Promise.all(labels.map(async (label) => {
      const cohort = usersByWeek[label];
      if (!cohort || cohort.length === 0) {
        return { cohortLabel: label, activationRatePct: 0 } as ActivationMetrics;
      }
      const userIds = cohort.map((u) => u.id);
      const chunks: string[][] = [];
      for (let i = 0; i < userIds.length; i += 10) chunks.push(userIds.slice(i, i + 10));
      const snapshots = await Promise.all(chunks.map((chunk) => getDocs(query(
        requestsRef,
        where('userId', 'in', chunk),
        where('status', '>=', RequestStatus['In Progress']),
      ))));
      const requestDocs: Array<{ id: string; userId: string; status: unknown; paidAt?: { toDate?: () => Date }; createdAt?: { toDate?: () => Date } }> = [];
      snapshots.forEach((s) => s.forEach((r) => requestDocs.push({ id: r.id, ...(r.data() as { userId: string; status: unknown; paidAt?: { toDate?: () => Date }; createdAt?: { toDate?: () => Date } }) })));

      let activated = 0;
      cohort.forEach((user) => {
        const firstPaid = requestDocs
          .filter((r) => r.userId === user.id && isRequestPaid(r.status))
          .map((r) => (r.paidAt?.toDate?.() || r.createdAt?.toDate?.() || null))
          .filter((x): x is Date => Boolean(x))
          .sort((a: Date, b: Date) => a.getTime() - b.getTime())[0] as Date | undefined;
        if (!firstPaid) return;
        const withinWindow = firstPaid.getTime() - user.regAt.getTime() <= windowDays * 24 * 60 * 60 * 1000;
        if (withinWindow) activated += 1;
      });

      return { cohortLabel: label, activationRatePct: Number(((activated / cohort.length) * 100).toFixed(2)) } as ActivationMetrics;
    }));

    KPIService.activationRatesCache.set(key, results as ActivationMetrics[]);
    return results;
  }

  static async getRetentionMetrics(weeks = 8, useCache = true): Promise<RetentionMetrics[]> {
    if (useCache && KPIService.retentionCache.has(weeks)) {
      const cached = KPIService.retentionCache.get(weeks);
      if (cached) return cached;
    }
    const now = new Date();
    const startBoundary = addDays(startOfWeek(now), -7 * (weeks - 1));
    const endBoundary = endOfDay(addDays(startOfWeek(now), 6));
    const earliestWindowStart = addDays(startBoundary, -29);

    const requestsRef = collection(db, 'requests');
    const snap = await getDocs(query(
      requestsRef,
      where('createdAt', '>=', toTimestamp(earliestWindowStart)),
      where('createdAt', '<=', toTimestamp(endBoundary)),
    ));
    const paidRangeSnap = await getDocs(query(
      requestsRef,
      where('paidAt', '>=', toTimestamp(earliestWindowStart)),
      where('paidAt', '<=', toTimestamp(endBoundary)),
    ));

    const wauPerWeek: Record<string, Set<string>> = {};
    const paidDatesByUser: Record<string, Date[]> = {};

    snap.forEach((docSnap) => {
      const data = docSnap.data();
      const createdAt = data.createdAt?.toDate?.();
      const userId = data.userId as string;
      if (!createdAt || !userId) return;
      const weekLabel = labelForWeek(startOfWeek(createdAt));
      if (!wauPerWeek[weekLabel]) wauPerWeek[weekLabel] = new Set<string>();
      wauPerWeek[weekLabel].add(userId);
    });
    paidRangeSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const paidAt = data.paidAt?.toDate?.();
      const userId = data.userId as string;
      if (!paidAt || !userId) return;
      if (!paidDatesByUser[userId]) paidDatesByUser[userId] = [];
      paidDatesByUser[userId].push(paidAt);
    });

    const usersCountSnap = await getCountFromServer(collection(db, 'users'));
    const totalUsers = usersCountSnap.data().count || 1;

    const results: RetentionMetrics[] = [];
    for (let i = weeks - 1; i >= 0; i -= 1) {
      const weekStart = addDays(startOfWeek(now), -7 * i);
      const weekEnd = endOfDay(addDays(weekStart, 6));
      const monthStart = addDays(weekEnd, -29);
      const weekLabel = labelForWeek(weekStart);
      const wau = (wauPerWeek[weekLabel]?.size) || 0;
      let monthlyBookers = 0;
      Object.keys(paidDatesByUser).forEach((uid) => {
        const dates = paidDatesByUser[uid];
        if (dates.some((d) => d >= monthStart && d <= weekEnd)) monthlyBookers += 1;
      });
      results.push({ weekLabel, wau, monthlyBookersPct: Number(((monthlyBookers / totalUsers) * 100).toFixed(2)) });
    }

    KPIService.retentionCache.set(weeks, results);
    return results;
  }

  static async getMonetization(days = 30, commissionRate = 0.15, useCache = true): Promise<MonetizationBreakdown> {
    const key = `${days}:${commissionRate}`;
    if (useCache && KPIService.monetizationCache.has(key)) {
      const cached = KPIService.monetizationCache.get(key);
      if (cached) return cached;
    }
    const end = endOfDay(new Date());
    const start = addDays(end, -(days - 1));
    const requestsRef = collection(db, 'requests');
    const snap = await getDocs(query(
      requestsRef,
      where('createdAt', '>=', toTimestamp(start)),
      where('createdAt', '<=', toTimestamp(end)),
      where('status', '>=', RequestStatus['In Progress']),
    ));
    let gmv = 0;
    snap.forEach((docSnap) => {
      const data = docSnap.data() as { payment?: { amount?: unknown } };
      const amount = Number(data?.payment?.amount || 0);
      if (!Number.isNaN(amount)) gmv += amount;
    });
    const netRevenue = Number((gmv * commissionRate).toFixed(2));
    const result = { gmv: Number(gmv.toFixed(2)), netRevenue };
    KPIService.monetizationCache.set(key, result);
    return result;
  }

  static async getTopline(useCache = true): Promise<{ totalUsers: number; totalRequests: number; paidRequests: number; conversionPaidPct: number }> {
    if (useCache && KPIService.toplineCache.data) return KPIService.toplineCache.data;
    const totalUsers = await UserService.getUsersCount();
    const totalRequestsSnap = await getCountFromServer(collection(db, 'requests'));
    const totalRequests = totalRequestsSnap.data().count || 0;
    const paidSnap = await getDocs(query(collection(db, 'requests'), where('status', 'in', [RequestStatus['In Progress'], RequestStatus.Completed])));
    const paidRequests = paidSnap.size || 0;
    const conversionPaidPct = totalUsers ? Number(((paidRequests / totalUsers) * 100).toFixed(2)) : 0;
    KPIService.toplineCache.data = {
      totalUsers, totalRequests, paidRequests, conversionPaidPct,
    };
    return KPIService.toplineCache.data;
  }

  static async getWeeklyRequests(weeks = 8, useCache = true): Promise<WeeklyRequestsMetrics[]> {
    if (useCache && KPIService.weeklyRequestsCache.has(weeks)) {
      const cached = KPIService.weeklyRequestsCache.get(weeks);
      if (cached) return cached;
    }
    const now = new Date();
    const startBoundary = addDays(startOfWeek(now), -7 * (weeks - 1));
    const endBoundary = endOfDay(addDays(startOfWeek(now), 6));
    const allSnap = await getDocs(query(
      collection(db, 'requests'),
      where('createdAt', '>=', toTimestamp(startBoundary)),
      where('createdAt', '<=', toTimestamp(endBoundary)),
    ));
    const paidSnap = await getDocs(query(
      collection(db, 'requests'),
      where('paidAt', '>=', toTimestamp(startBoundary)),
      where('paidAt', '<=', toTimestamp(endBoundary)),
    ));
    const buckets: Record<string, { total: number; paid: number }> = {};
    for (let i = 0; i < weeks; i += 1) {
      const ws = addDays(startOfWeek(now), -7 * i);
      buckets[labelForWeek(ws)] = { total: 0, paid: 0 };
    }
    allSnap.forEach((d) => {
      const data = d.data() as { createdAt?: { toDate?: () => Date } };
      const createdAt = data.createdAt?.toDate?.();
      if (!createdAt) return;
      const label = labelForWeek(startOfWeek(createdAt));
      if (!buckets[label]) return;
      buckets[label].total += 1;
    });
    paidSnap.forEach((d) => {
      const data = d.data() as { createdAt?: { toDate?: () => Date } };
      const createdAt = data.createdAt?.toDate?.();
      if (!createdAt) return;
      const label = labelForWeek(startOfWeek(createdAt));
      if (!buckets[label]) return;
      buckets[label].paid += 1;
    });
    const labels = Object.keys(buckets).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const result = labels.map((l) => ({ weekLabel: l, total: buckets[l].total, paid: buckets[l].paid }));
    KPIService.weeklyRequestsCache.set(weeks, result);
    return result;
  }

  static async getTopServicesWeekly(limitCount = 5, useCache = true): Promise<TopServiceWeeklyStat[]> {
    if (useCache && KPIService.topServicesCache.has(limitCount)) {
      const cached = KPIService.topServicesCache.get(limitCount);
      if (cached) return cached;
    }
    const weekStart = startOfWeek(new Date());
    const weekEnd = endOfDay(addDays(weekStart, 6));
    const allSnap = await getDocs(query(
      collection(db, 'requests'),
      where('createdAt', '>=', toTimestamp(weekStart)),
      where('createdAt', '<=', toTimestamp(weekEnd)),
    ));
    const paidSnapWeek = await getDocs(query(
      collection(db, 'requests'),
      where('createdAt', '>=', toTimestamp(weekStart)),
      where('createdAt', '<=', toTimestamp(weekEnd)),
      where('status', 'in', [RequestStatus['In Progress'], RequestStatus.Completed]),
    ));
    const counts: Record<string, { total: number; paid: number }> = {};
    allSnap.forEach((d) => {
      const data = d.data();
      const sId = data.serviceId || 'unknown';
      if (!counts[sId]) counts[sId] = { total: 0, paid: 0 };
      counts[sId].total += 1;
    });
    paidSnapWeek.forEach((d) => {
      const data = d.data();
      const sId = data.serviceId || 'unknown';
      if (!counts[sId]) counts[sId] = { total: 0, paid: 0 };
      counts[sId].paid += 1;
    });
    const entries = Object.entries(counts)
      .map(([serviceId, v]) => ({ serviceId, total: v.total, paid: v.paid }))
      .sort((a, b) => b.total - a.total)
      .slice(0, limitCount);

    // Map serviceId -> title
    try {
      const services = await ServiceService.getAll();
      const map: Record<string, string> = {};
      services.forEach((s: { docId: string; title: string }) => { map[s.docId] = s.title; });
      const result = entries.map((e) => ({ ...e, serviceTitle: map[e.serviceId] || e.serviceId })) as TopServiceWeeklyStat[];
      KPIService.topServicesCache.set(limitCount, result as TopServiceWeeklyStat[]);
      return result;
    } catch (_) {
      KPIService.topServicesCache.set(limitCount, entries as TopServiceWeeklyStat[]);
      return entries as TopServiceWeeklyStat[];
    }
  }

  static async getPaidServicesThisWeek(useCache = true): Promise<{ distinctCount: number; services: Array<{ serviceId: string; serviceTitle?: string; paid: number }> }> {
    if (useCache && KPIService.paidServicesThisWeekCache) return KPIService.paidServicesThisWeekCache;
    const weekStart = startOfWeek(new Date());
    const weekEnd = endOfDay(addDays(weekStart, 6));
    // Consider a request paid if it has paid=true or a paidAt timestamp
    const paidSnapWeek = await getDocs(query(
      collection(db, 'requests'),
      where('paidAt', '>=', toTimestamp(weekStart)),
      where('paidAt', '<=', toTimestamp(weekEnd)),
    ));
    const counts: Record<string, number> = {};
    paidSnapWeek.forEach((d) => {
      const data = d.data() as { serviceId?: string };
      const sId = data.serviceId || 'unknown';
      counts[sId] = (counts[sId] || 0) + 1;
    });
    const entries = Object.entries(counts)
      .map(([serviceId, paid]) => ({ serviceId, paid }))
      .sort((a, b) => b.paid - a.paid);
    try {
      const services = await ServiceService.getAll();
      const map: Record<string, string> = {};
      services.forEach((s: { docId: string; title: string }) => { map[s.docId] = s.title; });
      KPIService.paidServicesThisWeekCache = {
        distinctCount: paidSnapWeek.size,
        services: entries.map((e) => ({ ...e, serviceTitle: map[e.serviceId] || e.serviceId })),
      };
    } catch (_) {
      KPIService.paidServicesThisWeekCache = {
        distinctCount: paidSnapWeek.size,
        services: entries,
      };
    }
    return KPIService.paidServicesThisWeekCache;
  }

  private static goalsCache: GoalConfig | null = null;

  static async getGoalsConfig(useCache = true): Promise<GoalConfig> {
    if (useCache && KPIService.goalsCache) return KPIService.goalsCache;
    try {
      const docRef = doc(db, GOALS_COLLECTION, GOALS_DOC_ID);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        KPIService.goalsCache = snap.data() as GoalConfig;
        return KPIService.goalsCache;
      }
    } catch (_) {
      // ignore
    }
    // defaults
    KPIService.goalsCache = {
      weeklyInstallsTarget: 200,
      activationRateTargetPct: 35,
      wauTarget: 300,
      monthlyBookersTargetPct: 15,
      gmvTarget: 5000,
      netRevenueTarget: 750,
    };
    return KPIService.goalsCache;
  }

  static async saveGoalsConfig(config: GoalConfig): Promise<void> {
    const docRef = doc(db, GOALS_COLLECTION, GOALS_DOC_ID);
    await setDoc(docRef, config, { merge: true });
    KPIService.goalsCache = config;
  }

  static async getPlatformBreakdown(useCache = true): Promise<PlatformBreakdown> {
    if (useCache && KPIService.platformCache) return KPIService.platformCache;
    const usersRef = collection(db, 'users');
    const [iosSnap, androidSnap] = await Promise.all([
      getDocs(query(usersRef, where('platform', '==', 'ios'))),
      getDocs(query(usersRef, where('platform', '==', 'android'))),
    ]);
    KPIService.platformCache = { ios: iosSnap.size, android: androidSnap.size };
    return KPIService.platformCache;
  }

  static async getAcquisitionChannelBreakdown(useCache = true): Promise<AcquisitionChannelBreakdown> {
    if (useCache && KPIService.acquisitionChannelCache) return KPIService.acquisitionChannelCache;
    const usersSnap = await getDocs(collection(db, 'users'));
    const counts: AcquisitionChannelBreakdown = {};
    usersSnap.forEach((docSnap) => {
      const data = docSnap.data() as { acquisitionChannel?: string };
      if (!data.acquisitionChannel) return;
      const channel = data.acquisitionChannel.toString().trim();
      counts[channel] = (counts[channel] || 0) + 1;
    });
    KPIService.acquisitionChannelCache = counts;
    return counts;
  }
}

export default KPIService;
